import React, { useState, useReducer, useEffect, useContext } from "react"
import dynamic from "next/dynamic"
import clsx from "clsx"
import { Box, Button, Container, CssBaseline, Theme } from "@mui/material"
import { useTour } from "@reactour/tour"

import { makeStyles } from "makeStyles"

import SEO from "@secureo/common/components/SEO"
import LockingSystemHeader from "./Layout/LockingSystemHeader"
import LockingSystemSidebar, { sidebarWidthPx } from "./Layout/LockingSystemSidebar"
import LockingSystemQuickStart from "./LockingSystemQuickStart"
import LockingSystemMatrix from "./Matrix/LockingSystemMatrix"
import LockingSystemComponentOverview from "./Overview/LockingSystemComponentOverview"

const EditLockingSystemComponent = dynamic(
	() => import("./EditComponent/EditLockingSystemComponent"),
)
const ChangeNameModal = dynamic(() => import("containers/LockingSystem/ChangeNameModal"))
const ShareModal = dynamic(() => import("containers/LockingSystem/ShareModal"))
const LockingSystemDebugging = dynamic(
	() => import("containers/LockingSystem/LockingSystemDebugging"),
)

import useLockingSystemPreviewProducts from "@secureo/common/hooks/data/useLockingSystemPreviewProducts"
import useLockingSystemKeyboardShortcuts from "hooks/lockingSystem/useLockingSystemKeyboardShortcuts"
import useNavigationPreventionOnUnsavedChanges from "hooks/lockingSystem/useNavigationPreventionOnUnsavedChanges"
import useToggle from "@secureo/common/hooks/useToggle"
import { useSnackbar } from "notistack"

import reducer, {
	LockingSystemReducerState,
	DispatchLockingSystemReducer,
	resetReducerState,
	addDefaultLockingComponent,
	addDefaultKeyComponent,
	setCellValues,
	deleteLockingComponents,
	deleteKeyComponents,
	setComponentName,
	setComponentQuantity,
	duplicateLockingComponent,
	duplicateKeyComponent,
	setSystemName,
	setComponentLockingSystemProduct,
	setSelectedComponentSystem,
	setSelectedComponentOuterLength,
	setSelectedComponentInnerLength,
	addInitialComponents,
	setSpecialMatrixType,
	SpecialMatrixType,
	setUpdatedComponent,
	moveComponent,
	MoveDirection,
	saveLockingSystemConfiguration,
	addToCart,
	toggleLockingComponentSelections,
	toggleKeyComponentSelection,
} from "reducers/lockingSystemReducer"
import getInitialReducerState from "reducers/lockingSystemReducer/utils/getInitialReducerState"
import wait from "@secureo/common/utils/wait"
import fetchSavedLockingSystem from "utils/api/lockingSystem/fetchSavedLockingSystem"

import { CountryCode } from "@secureo/common/typings/CountryCode"
import { LockingSystemCategory } from "@secureo/common/typings/Category"
import {
	LockingSystemComponent,
	LockingSystemPreviewProduct,
	SavedLockingSystem,
} from "@secureo/common/typings/LockingSystem"
import AppContext from "@secureo/common/context/AppContext/AppContext"
import {
	hasCompletedTourGuide,
	setHasCompletedTourGuide,
} from "reducers/lockingSystemReducer/utils/rememberTourGuideCompletion"
import isValidMatrix from "@secureo/common/utils/isValidMatrix"
import { CurrencyCode } from "@secureo/common/typings/Forex"

const useStyles = makeStyles()((theme: Theme) => ({
	root: {
		display: "flex",
	},
	appBar: {
		background: "#263739",
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
	},
	appBarShift: {
		width: `calc(100% - ${sidebarWidthPx}px)`,
		marginLeft: sidebarWidthPx,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	},
	drawer: {
		width: sidebarWidthPx,
		flexShrink: 0,
	},
	drawerPaper: {
		width: sidebarWidthPx,
		paddingLeft: 10,
		background:
			"linear-gradient(61deg, rgba(67,138,196,1) 41%, rgba(68,141,200,1) 41%, rgba(68,141,200,1) 55%, rgba(70,145,206,1) 55%);",
		color: "white",
	},
	drawerHeader: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		// necessary for content to be below app bar
		// ...theme.mixins.toolbar,
		justifyContent: "flex-end",
	},
	content: {
		flexGrow: 1,
		padding: theme.spacing(3),
		transition: theme.transitions.create("margin", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		marginLeft: -sidebarWidthPx,
	},
	contentShift: {
		transition: theme.transitions.create("margin", {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
		marginLeft: 0,
	},
})) as any

export type LockingSystemConfiguratorView = "matrix" | "overview"

// TODO: Prevent navigate away on unsaved changes, ask to save first
const LockingSystemConfigurator = ({
	accessToken,
	asPath,
	lockingSystemCategory,
	locale,
	countryCode,
	currencyCode = "EUR",
	isDebug = false,
}: Props) => {
	const { classes } = useStyles()
	const { enqueueSnackbar } = useSnackbar()
	const appContext = useContext(AppContext)
	const { setIsOpen: setTourGuideIsOpen } = useTour()

	const [view, setView] = useState("matrix" as LockingSystemConfiguratorView)
	const [sidebarIsOpen, toggleSidebarIsOpen] = useToggle(true)
	const [changeNameModalIsOpen, toggleChangeNameModalIsOpen] = useToggle(false)
	const [shareModalIsOpen, toggleShareModalIsOpen] = useToggle(false)
	const [editComponentModalUuid, setEditComponentModalUuid] = useState(null as string)

	const [reducerState, dispatch] = useReducer(
		reducer,
		getInitialReducerState(lockingSystemCategory, locale, countryCode, isDebug),
	) as [LockingSystemReducerState, DispatchLockingSystemReducer]

	useNavigationPreventionOnUnsavedChanges(reducerState?.hasUnsavedChanges ?? false)

	const { lockingSystemPreviewProductsByTypeBySku } = useLockingSystemPreviewProducts(
		accessToken,
		lockingSystemCategory?.id,
		currencyCode,
	)
	const availableLockingComponents = Object.values(
		lockingSystemPreviewProductsByTypeBySku?.lock,
	).sort((a, b) => a.name.localeCompare(b.name))
	const availableKeyComponents = Object.values(lockingSystemPreviewProductsByTypeBySku?.key).sort(
		(a, b) => a.name.localeCompare(b.name),
	)

	const resetLockingSystem = async (initiatedByUser = false) => {
		const isDebug = window?.location?.href?.includes("?debug")
		const lockingSystemShoppingListId = new URLSearchParams(window.location.search).get("id")

		let savedLockingSystem: SavedLockingSystem = null
		let isOwner = true

		if (lockingSystemShoppingListId) {
			if (initiatedByUser) {
				dispatch({
					type: "SET_IS_LOADING",
					payload: true,
				})
			}
			enqueueSnackbar("Bitte warten Sie während ihr Schließplan geladen wird", {
				variant: "info",
			})
			fetchSavedLockingSystem(accessToken, lockingSystemShoppingListId)
				.then((response) => {
					if (response) {
						savedLockingSystem = response.savedLockingSystem
						isOwner = response.isOwner
					} else {
						enqueueSnackbar("Schließplan konnte nicht geladen werden", {
							variant: "error",
						})
					}
				})
				.catch((err) => {
					enqueueSnackbar("Schließplan konnte nicht geladen werden", {
						variant: "error",
					})
					console.error(err)
				})
				.finally(() => {
					resetReducerState(
						accessToken,
						dispatch,
						lockingSystemCategory,
						locale,
						countryCode,
						isDebug,
						savedLockingSystem,
						isOwner,
					)
					if (initiatedByUser) {
						enqueueSnackbar("Schließplan auf gespeicherte Version zurückgesetzt", {
							variant: "success",
						})
					} else {
						if (savedLockingSystem) {
							enqueueSnackbar("Schließplan geladen", {
								variant: "success",
							})
						}
					}
				})
		} else {
			resetReducerState(
				accessToken,
				dispatch,
				lockingSystemCategory,
				locale,
				countryCode,
				isDebug,
			)
			if (initiatedByUser) {
				enqueueSnackbar("Schließplan zurückgesetzt", {
					variant: "info",
				})
			}
		}
	}

	const startTourGuideIfHasNotBeenCompleted = () => {
		if (!hasCompletedTourGuide()) {
			startTourGuide()
			// enqueueSnackbar("Neu hier?", {
			// 	variant: "info",
			// 	// eslint-disable-next-line react/display-name
			// 	action: () => {
			// 		return (
			// 			<Button
			// 				onClick={() => {
			// 					startTourGuide()
			// 				}}
			// 				size="small"
			// 				color="secondary"
			// 				style={{ textTransform: "inherit", color: "#fff" }}
			// 			>
			// 				Tourguide starten
			// 			</Button>
			// 		)
			// 	},
			// })
		}
	}

	useEffect(() => {
		// Reset reducer state when lockingSystemCategory changes
		if (accessToken) {
			resetLockingSystem()
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lockingSystemCategory?.id, accessToken])

	const toggleIsQuickStartOpen = () => {
		dispatch({ type: "TOGGLE_QUICK_START_IS_OPEN" })
	}

	const startTourGuide = async () => {
		if (sidebarIsOpen) {
			toggleSidebarIsOpen()
			setView("matrix")
			await wait(200)
		}
		setTourGuideIsOpen(true)
		setHasCompletedTourGuide()
	}

	const {
		name,
		isFrozen,
		// hasUnsavedChanges,
		shoppingListId,
		isLoading,
		matrix,
		matrixType,
		lockingComponentsUuids,
		keyComponentsUuids,
		componentsByUuid,
		taxRate,
		system,
		// totalGrossMsrpPriceCents,

		totalGrossPriceCents,
		charges,
		isQuickStartOpen,
	} = reducerState

	// Functionality
	const addDefaultLock = (count = 1) => {
		addDefaultLockingComponent(dispatch, reducerState, count)
	}

	const duplicateLock = (uuid: string) => {
		duplicateLockingComponent(dispatch, reducerState, uuid)
	}

	const addDefaultKey = (count = 1) => {
		addDefaultKeyComponent(dispatch, reducerState, count)
	}

	const duplicateKey = (uuid: string) => {
		duplicateKeyComponent(dispatch, reducerState, uuid)
	}

	const setSpecialMatrix = (specialMatrixType: SpecialMatrixType) => {
		setSpecialMatrixType(dispatch, matrix, specialMatrixType)
	}

	const removeLock = (uuid: string) => {
		deleteLockingComponents(dispatch, reducerState, [uuid])
	}

	const removeKey = (uuid: string) => {
		deleteKeyComponents(dispatch, reducerState, [uuid])
	}

	const setCellValue = (rowIndex: number, cellIndex: number, isSelected: boolean) => {
		setCellValues(dispatch, reducerState, [[rowIndex, cellIndex, isSelected]])
	}

	const toggleRowSelections = (uuid: string) => {
		toggleLockingComponentSelections(dispatch, reducerState, uuid)
	}

	const toggleColumnSelections = (uuid: string) => {
		toggleKeyComponentSelection(dispatch, reducerState, uuid)
	}

	const setLockingSystemName = (lockingSystemName: string) => {
		setSystemName(dispatch, lockingSystemName)
	}

	const setSystemComponentName = (uuid: string, name: string) => {
		setComponentName(dispatch, uuid, name)
	}

	const setSystemComponentQuantity = (uuid: string, quantity: number) => {
		setComponentQuantity(dispatch, reducerState, uuid, quantity)
	}

	const setComponentSystem = (uuid: string, selectedSystem: string) => {
		setSelectedComponentSystem(
			accessToken,
			reducerState,
			dispatch,
			uuid,
			selectedSystem,
			enqueueSnackbar,
		)
	}

	const setComponentOuterLength = (uuid: string, selectedOuterLength: number) => {
		setSelectedComponentOuterLength(
			accessToken,
			reducerState,
			dispatch,
			uuid,
			selectedOuterLength,
			enqueueSnackbar,
		)
	}

	const setComponentInnerLength = (uuid: string, selectedInnerLength: number) => {
		setSelectedComponentInnerLength(
			accessToken,
			reducerState,
			dispatch,
			uuid,
			selectedInnerLength,
			enqueueSnackbar,
		)
	}

	const setComponentProduct = (
		uuid: string,
		lockingSystemPreviewProduct: LockingSystemPreviewProduct,
	) => {
		setComponentLockingSystemProduct(
			accessToken,
			dispatch,
			reducerState,
			uuid,
			lockingSystemPreviewProduct,
			enqueueSnackbar,
		)
	}

	const updateComponent = (updatedComponent: LockingSystemComponent) => {
		setUpdatedComponent(dispatch, reducerState, updatedComponent)
	}

	const moveMatrixComponent = (uuid: string, direction: MoveDirection) => {
		moveComponent(dispatch, reducerState, uuid, direction)
	}

	const createInitialComponents = (
		numberOfLockingComponents: number,
		numberOfKeyComponents: number,
	) => {
		addInitialComponents(
			dispatch,
			reducerState,
			numberOfLockingComponents,
			numberOfKeyComponents,
		)
	}

	const saveLockingSystem = async () => {
		const lockingSystemId = await saveLockingSystemConfiguration(
			accessToken,
			dispatch,
			reducerState,
			currencyCode,
		)
		enqueueSnackbar("Schließplan gespeichert", {
			variant: "success",
		})

		return lockingSystemId
	}

	const duplicateLockingSystem = async () => {
		const lockingSystemId = await saveLockingSystemConfiguration(
			accessToken,
			dispatch,
			{ ...reducerState, shoppingListId: null, shoppingListVersion: null },
			currencyCode,
		)
		enqueueSnackbar("Schließplan dupliziert", {
			variant: "success",
		})

		return lockingSystemId
	}

	const addLockingSystemToCart = () => {
		if (isValidMatrix(matrix)) {
			addToCart(dispatch, reducerState, appContext, enqueueSnackbar, saveLockingSystem)
		} else {
			enqueueSnackbar(
				"Bitte wählen Sie für jede Spalte und Zeile mindestens eine Berechtigung",
				{ variant: "warning" },
			)
			setView("matrix")
		}
	}

	useLockingSystemKeyboardShortcuts(
		{
			addDefaultLock,
			duplicateLock,
			addDefaultKey,
			duplicateKey,
			setSpecialMatrix,
		},
		lockingComponentsUuids,
		keyComponentsUuids,
		!isLoading,
	)

	const title = `${name ? `${name} | ` : ""}${lockingSystemCategory.name} Schließanlage planen`

	const { deliveryTimeWorkDays } = lockingSystemCategory

	return (
		<>
			<SEO asPath={asPath} title={title} appendLocalizedShopNameWithPipe noindex />

			<div className={classes.root}>
				<CssBaseline />
				<LockingSystemHeader
					title={title}
					sidebarIsOpen={sidebarIsOpen}
					toggleSidebarIsOpen={toggleSidebarIsOpen}
					toggleChangeNameModalIsOpen={toggleChangeNameModalIsOpen}
					totalGrossPriceCents={totalGrossPriceCents + charges.totalGrossChargeCents}
					view={view}
					setView={setView}
					isFrozen={isFrozen}
					isLoading={isLoading}
					addToCart={addLockingSystemToCart}
				/>

				<LockingSystemSidebar
					sidebarIsOpen={sidebarIsOpen}
					toggleSidebarIsOpen={toggleSidebarIsOpen}
					view={view}
					setView={setView}
					lockingSystemCategorySlug={lockingSystemCategory.slug}
					startTourGuide={startTourGuide}
					matrixType={matrixType}
					countryCode={countryCode}
				/>

				<Box
					mt={5}
					className={clsx(classes.content, {
						[classes.contentShift]: sidebarIsOpen,
					})}
					style={{ background: "rgba(1, 1, 1, 0.03)", minHeight: "100vh" }}
				>
					<Box pb={5}>
						<Container maxWidth="xl">
							<Box my={2}>
								{view === "matrix" && (
									<LockingSystemMatrix
										isFrozen={isFrozen}
										isLoading={!accessToken || isLoading}
										lockingSystemCategory={lockingSystemCategory}
										matrixType={matrixType}
										matrix={matrix}
										lockingComponentsUuids={lockingComponentsUuids}
										keyComponentsUuids={keyComponentsUuids}
										componentsByUuid={componentsByUuid}
										charges={charges}
										availableLockingComponents={availableLockingComponents}
										lockingSystemId={shoppingListId}
										locale={locale}
										addDefaultLockingComponent={addDefaultLock}
										addDefaultKeyComponent={addDefaultKey}
										setCellValue={setCellValue}
										removeLock={removeLock}
										removeKey={removeKey}
										duplicateKey={duplicateKey}
										duplicateLock={duplicateLock}
										setComponentName={setSystemComponentName}
										setComponentLockingSystemProduct={setComponentProduct}
										setComponentQuantity={setSystemComponentQuantity}
										setComponentOuterLength={setComponentOuterLength}
										setComponentInnerLength={setComponentInnerLength}
										setEditComponentModalUuid={setEditComponentModalUuid}
										moveMatrixComponent={moveMatrixComponent}
										setSpecialMatrix={setSpecialMatrix}
										saveLockingSystem={saveLockingSystem}
										duplicateLockingSystem={duplicateLockingSystem}
										toggleShareModalIsOpen={toggleShareModalIsOpen}
										resetLockingSystem={resetLockingSystem}
										toggleKeyComponentSelection={toggleColumnSelections}
										toggleLockingComponentSelections={toggleRowSelections}
									/>
								)}

								{view === "overview" && (
									<LockingSystemComponentOverview
										matrix={matrix}
										matrixType={matrixType}
										isLoading={isLoading}
										isFrozen={isFrozen}
										lockingComponentsUuids={lockingComponentsUuids}
										keyComponentsUuids={keyComponentsUuids}
										componentsByUuid={componentsByUuid}
										taxRate={taxRate}
										setComponentQuantity={setSystemComponentQuantity}
										setEditComponentModalUuid={setEditComponentModalUuid}
										availableLockingComponents={availableLockingComponents}
										availableKeyComponents={availableKeyComponents}
										charges={charges}
										setSystemComponentName={setSystemComponentName}
										setComponentProduct={setComponentProduct}
										setComponentSystem={setComponentSystem}
										setComponentOuterLength={setComponentOuterLength}
										setComponentInnerLength={setComponentInnerLength}
										saveLockingSystem={saveLockingSystem}
										toggleShareModalIsOpen={toggleShareModalIsOpen}
										totalGrossPriceCents={
											totalGrossPriceCents + charges.totalGrossChargeCents
										}
										deliveryTimeWorkDays={deliveryTimeWorkDays}
									/>
								)}
							</Box>
						</Container>
					</Box>
				</Box>
			</div>

			{isQuickStartOpen && (
				<LockingSystemQuickStart
					closeModal={toggleIsQuickStartOpen}
					setLockingSystemName={setLockingSystemName}
					createInitialComponents={createInitialComponents}
					startTourGuideIfHasNotBeenCompleted={startTourGuideIfHasNotBeenCompleted}
				/>
			)}

			{changeNameModalIsOpen && (
				<ChangeNameModal
					closeModal={toggleChangeNameModalIsOpen}
					lockingSystemName={name}
					setLockingSystemName={setLockingSystemName}
				/>
			)}

			{shareModalIsOpen && (
				<ShareModal
					toggleShareModalIsOpen={toggleShareModalIsOpen}
					shoppingListId={shoppingListId}
					saveLockingSystem={saveLockingSystem}
					locale={locale}
					lockingSystemCategorySlug={system}
					enqueueSnackbar={enqueueSnackbar}
				/>
			)}

			{editComponentModalUuid && (
				<EditLockingSystemComponent
					component={componentsByUuid?.[editComponentModalUuid]}
					charges={charges?.byUuid?.[editComponentModalUuid]}
					removeLock={removeLock}
					removeKey={removeKey}
					setUpdatedComponent={updateComponent}
					closeModal={() => {
						setEditComponentModalUuid(null)
					}}
					availableLockingComponents={availableLockingComponents}
					availableKeyComponents={availableKeyComponents}
					accessToken={accessToken}
					locale={locale}
					countryCode={countryCode}
					taxRate={taxRate}
					isFrozen={isFrozen}
				/>
			)}

			{isDebug && (
				<LockingSystemDebugging
					lockingSystemPreviewProductsByTypeBySku={
						lockingSystemPreviewProductsByTypeBySku
					}
				/>
			)}
		</>
	)
}

interface Props {
	accessToken: string
	asPath: string
	locale: string
	countryCode: CountryCode
	lockingSystemCategory: LockingSystemCategory
	isDebug?: boolean
	savedLockingSystem?: unknown
	currencyCode?: CurrencyCode
}

export default LockingSystemConfigurator
